import { stAnalytics } from "@storytell/analytics";
import {
  type AuthServiceMethods,
  type AuthStore,
  createTokenRefresher,
  firebaseConfig,
  initialAuthStore,
  prepareSyncRequest,
} from "@storytell/auth";
import { useAuthClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { SyncResponse } from "@storytell/cp-client/src/gen/auth/v3/auth_pb";
import { type Logger, Named } from "@storytell/logger";
import { type Result, isError, isSuccess, newErrorResult, newSuccessResult } from "@storytell/result";
import { BrowserStorage } from "@storytell/storage";
import { initializeApp } from "firebase/app";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  type User,
  type UserCredential,
  signOut as firebaseSignOut,
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithRedirect,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithCredential,
} from "firebase/auth";
import { onCleanup, onMount, type Accessor, type InitializedResourceReturn } from "solid-js";
import { isServer } from "solid-js/web";

declare const APP_DOMAIN: string;

export const BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL = "lumle_v2";

/**
 * Implementation for the auth methods that are used in browser-based auth.
 * */
export const useAuthBrowserMethods = (deps: {
  logger: Logger;
  resource: InitializedResourceReturn<AuthStore, unknown>;
  deviceId: Accessor<string>;
  locale: Accessor<string>;
  userAgent: Accessor<string>;
}): AuthServiceMethods => {
  const logger = new Named(deps.logger, "authBrowser");
  const { resource, deviceId, locale, userAgent } = deps;
  const [data, { mutate }] = resource;

  const setToken = (token: string) => mutate((p) => ({ ...p, token }));

  const firebaseInstance = initializeApp(firebaseConfig());
  const auth = getAuth(firebaseInstance);
  const refresher = createTokenRefresher(auth, (token: string) => setToken(token), logger);

  onMount(() => {
    if (isServer) return;
    refresher.schedule(5 * 60);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        logger.info("tab became active, refreshing auth token");
        refresher.refresh(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    onCleanup(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      refresher.clear();
    });
  });

  const syncAuthWithAPI = async (firebaseUser: User): Promise<Result<SyncResponse>> => {
    logger.info("syncAuthWithAPI", { firebaseUser });

    const result = await useAuthClient().sync(
      prepareSyncRequest(firebaseUser, deviceId(), locale(), userAgent()),
      useCallOptions(withBearerToken(data().token)),
    );

    if (result.isOk()) {
      if (result.value.tokenRefreshReason) {
        logger.info("token refresh requested by server, refreshing token");
        const newToken = await firebaseUser.getIdToken(true);
        setToken(newToken);
      }
    }

    if (result.isErr()) {
      return newErrorResult(result.error.code, result.error.message);
    }

    return newSuccessResult(result.value);
  };

  const signInWithMagicLink = async (email: string, invite?: { type: "project"; inviteUrl: string }) => {
    try {
      stAnalytics.track("log_in_started", { email: email, flow: "magic-link" });
    } catch (error) {
      console.error(error);
    }
    auth.languageCode = deps.locale();
    await sendSignInLinkToEmail(auth, email, {
      url: `https://${APP_DOMAIN}/auth/finish${invite ? `?project-invite=${encodeURIComponent(invite.inviteUrl)}` : ""}`,
      handleCodeInApp: true,
    }).then(() => {
      BrowserStorage.set(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL, email);
    });
  };

  // finishSignInMagicLink finishes the sign in process for a magic link.
  // This function always runs on the client.
  const finishSignInMagicLink = async (email: string, url: string): Promise<Result<null>> => {
    const childLogger = logger.child("finishSignInMagicLink");

    await auth.authStateReady().catch(() => null);

    // Reset auth store state
    mutate(initialAuthStore());

    // Validate current user state
    if (!auth.currentUser) {
      return newErrorResult("user_not_found", "User not found", "User not found");
    }

    if (!auth.currentUser.isAnonymous) {
      // TODO: i18n
      return newSuccessResult(null, undefined, "Already signed in", "current user is not anonymous");
    }

    // Create email credential and attempt to link/sign in
    const emailCredential = EmailAuthProvider.credentialWithLink(email, url);
    let credential: UserCredential;
    let didSignUp = false;

    try {
      credential = await linkWithCredential(auth.currentUser, emailCredential);
      didSignUp = true;
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (error: any) {
      if (error.code === "auth/email-already-in-use") {
        credential = await signInWithCredential(auth, emailCredential);
        didSignUp = false;
      } else {
        return newErrorResult(error.code, error.message, error.details);
      }
    }

    // Clean up stored email
    BrowserStorage.remove(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL);

    // Get and set token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return newErrorResult("token_not_found", "Token not found", "Token not found");
    }
    setToken(token);

    // Sync with API
    const syncResult = await syncAuthWithAPI(credential.user);
    if (isError(syncResult)) {
      return newErrorResult(syncResult.code, syncResult.message, syncResult.detail);
    }

    // Handle successful sync
    const authenticatedUser = syncResult.data.authenticatedUser;
    try {
      await stAnalytics.track(didSignUp ? "sign_up_successful" : "log_in_successful", {
        email: authenticatedUser.user.primaryEmail,
        userId: authenticatedUser.user.primaryEmail,
      });
      if (syncResult.data.authenticatedUser.signedUp) {
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        (window as any).dataLayer?.push("event", "conversion_event_signup", {
          email: authenticatedUser.user.primaryEmail,
          userId: authenticatedUser.user.id,
        });
      }
    } catch (error) {
      // TODO: capture with sentry while not allowing the user to be impacted
      childLogger.warn("Failed to track analytics", { error });
    }

    // Update auth store
    mutate((p) => ({
      ...p,
      state: authenticatedUser.user.lifecycleStatus,
      authenticatedUser,
      error: undefined,
    }));

    return newSuccessResult(null);
  };

  const isURLMagicLink = (url: string) => isSignInWithEmailLink(auth, url);

  const signInWithGoogle = async () => {
    await auth.authStateReady().catch(() => null);

    if (!auth.currentUser) {
      return;
    }

    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    auth.languageCode = deps.locale();
    await linkWithRedirect(auth.currentUser, provider);
  };

  const processOAuthRedirect = async () => {
    const childLogger = logger.child("processOAuthRedirect");

    let result: UserCredential | null = null;
    await auth.authStateReady().catch(() => null);

    let didSignUp = false;
    try {
      result = await getRedirectResult(auth);
      didSignUp = true;
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (error: any) {
      // INFO: Handle multiple errors here when we add providers other than google
      // https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#getredirectresult
      if (error.code === "auth/credential-already-in-use") {
        childLogger.info("credential already in use", { error });
        // The docs are wrong about this, there's no error.credential, but there's an error.customData
        const googleCredential = GoogleAuthProvider.credential(
          error.customData._tokenResponse.oauthIdToken,
          error.customData._tokenResponse.oauthAccessToken,
        );
        result = await signInWithCredential(auth, googleCredential);
        didSignUp = false;
      } else {
        throw error;
      }
    }
    if (!result) {
      return;
    }

    logger.info("OAuthRedirect obtained result", { result });

    const credential = GoogleAuthProvider.credentialFromResult(result);

    if (!credential) {
      return;
    }

    // establish the initial identity token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return;
    }
    setToken(token);

    try {
      await stAnalytics.track(didSignUp ? "sign_up_successful" : "log_in_successful", {
        email: result.user.email,
        userId: result.user.uid,
      });
    } catch (error) {
      // todo: capture with sentry but don't allow errors to impact user
    }

    const syncResponse = await syncAuthWithAPI(result.user);
    childLogger.info("response from syncAuthWithAPI", { syncResponse });

    if (isSuccess(syncResponse)) {
      const authenticatedUser = syncResponse.data.authenticatedUser;
      try {
        if (didSignUp) {
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (window as any).dataLayer?.push("event", "conversion_event_signup", {
            email: authenticatedUser.user.primaryEmail,
            userId: authenticatedUser.user.id,
          });
        }
      } catch (error) {}
      mutate((p) => ({
        ...p,
        state: authenticatedUser.user.lifecycleStatus,
        authenticatedUser,
        error: undefined,
      }));
    }

    return true;
  };

  const onUnauthenticated = async () => {
    logger.info("signOut");
    mutate(initialAuthStore());
    await signInAnonymously(auth);
  };

  const setupListeners = async () => {
    if (isServer) return;

    let didProcessOAuthRedirect = await processOAuthRedirect();

    let lastUserId: string | null = null;

    onAuthStateChanged(auth, async (user: User | null) => {
      if (!user) {
        await onUnauthenticated();
        return;
      }

      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        return;
      }
      setToken(token);

      if (user.uid === lastUserId) {
        return;
      }

      lastUserId = user.uid;

      if (didProcessOAuthRedirect) {
        didProcessOAuthRedirect = false;
        return;
      }

      const syncResponse = await syncAuthWithAPI(user);
      if (isSuccess(syncResponse)) {
        const authenticatedUser = syncResponse.data.authenticatedUser;
        mutate((p) => ({
          ...p,
          state: authenticatedUser.user.lifecycleStatus,
          authenticatedUser,
          error: undefined,
        }));
      }
    });
  };

  const signOut = () => {
    return firebaseSignOut(auth).then((r) => {
      try {
        stAnalytics.plugins.posthog.reset();
      } catch (error) {
        // TODO: capture with sentry but don't allow errors to impact user
      }

      window.location.href = "/";
      return r;
    });
  };

  return {
    signInWithMagicLink,
    signOut,
    setupListeners,
    signInWithGoogle,
    processOAuthRedirect,
    finishSignInMagicLink,
    isURLMagicLink,
  };
};
